export enum EducationLevel {
  BACHELORS = 'Bachelors',
  MASTERS = 'Masters',
  PHD = 'Phd',
}

export interface YearOfExperienceRange {
  min?: number;
  max?: number;
}

export interface SkillYoe {
  skill: string;
  years: number;
}

/** Domain filter for /find API (e.g. SWE, STEM, FINANCE). */
export type DomainFilter = string;

// Frontend filter structure (for UI). All criteria are hard (must-have); no criteriaType.
export interface SearchFilters {
  searchQuery?: string; // Free text search query (not sent to backend)
  domain?: DomainFilter; // STEM | SWE; sent to backend (default SWE)
  countries?: string[];
  usState?: string[]; // Admin-only US-state buckets: New York/California/Massachusetts/Texas/Washington/Rest of US
  yearOfExperience?: YearOfExperienceRange;
  hourlyRate?: YearOfExperienceRange;
  education?: EducationLevel[]; // Bachelors, Masters, PhD (maps to backend education)
  school?: string; // Single school name (maps to backend school)
  certificates?: string;
  skills?: SkillSearchFilter[]; // Skill names only (pills)
  researchPapersCount?: number;
  naturalLanguages?: string[];
  programmingLanguages?: string[];
  companies?: string[];
  typeOfWork?: string; // Expertise filter (e.g., RL GYM, MLE bench, SWE bench, etc.)
  field?: string; // Field filter for STEM (e.g., Physics, Chemistry, Mathematics, Biology)
  domainFilter?: string; // Domain filter for Finance (e.g., Investment Banking, Private Equity) — checked against tags in ES
  excludeViewed?: boolean;
  excludeSaved?: boolean;
  excludeDiscarded?: boolean;
  onlyViewed?: boolean;
  onlySaved?: boolean;
  onlyDiscarded?: boolean;
  // Temporal / extended facets extracted from NL queries (hydrated from appliedFilters).
  pastCompanies?: string[];
  currentCompanies?: string[];
  titles?: string[];
  hasGithubData?: boolean;
  // Negation facets.
  countriesExclude?: string[];
  companiesExclude?: string[];
  skillsExclude?: string[];
  talentIds?: number[];
  packetIds?: number[];
  page?: number;
  pageSize?: number;
}

// Skill search filter (name only; backend accepts optional yearsOfExperience but we don't use it)
export interface SkillSearchFilter {
  name?: string;
}

export interface Languages {
  natural: string[];
  programming: string[];
}

/** Pedigree signals returned by list API (school + companies). */
export interface PedigreeSignals {
  topSchool?: string | null;
  notableCompanies?: string[] | null;
}

/** Bay Area Talent Signals evidence (topSchools from list API). */
export interface BayAreaTalentSignals {
  evidence?: {
    topSchools?: Array<{ name?: string; abbrev?: string }>;
  };
}

/** Work experience entry from list/detail API. */
export interface WorkExperience {
  position?: string;
  company?: string;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
  isCurrent?: boolean;
  description?: string[];
}

/** Engineer as returned by POST /swe-search/find (list API). Full profile is GET /developers/:id (DeveloperDetails). */
export interface SoftwareTalent {
  id: number;
  name: string;
  title?: string;
  country: string;
  yearOfExperience: number;
  skills: (string | { name?: string })[];
  /** Generated/curated technical skills (prefer over skills for display). */
  tSkills?: string[] | null;
  score?: number;
  tShortSummary?: string;
  pedigreeSignals?: PedigreeSignals | null;
  /** Bay Area Talent Signals (evidence.topSchools from list API). */
  bayAreaTalentSignals?: BayAreaTalentSignals | null;
  /** Top performer at Turing (from list API). */
  topPerformer?: boolean;
  /** CFA charterholder flag. */
  isCFA?: boolean;
  /** LinkedIn verified (1 or 0). */
  linkedInVerify?: number | null;
  /** LinkedIn profile URL. */
  linkedInProfile?: string | null;
  /** LinkedIn verifications from Apify (joinedDate + Identity/Workplace/Education arrays). */
  linkedInVerification?: {
    joinedDate?: string | null;
    Identity?: Array<{ Value?: string; Date?: string }> | null;
    Workplace?: Array<{ Value?: string; Date?: string }> | null;
    EducationalInstitution?: Array<{ Value?: string; Date?: string }> | null;
  } | null;
  /** Hourly rate in USD/hr (null if unknown). */
  hourlyRate?: number | null;
  /** Work experience for credibility line (top company • title • yoe). */
  workExperience?: WorkExperience[];
  /** Whether this talent has been saved by the current user. */
  isSaved?: boolean;
  /** Whether this talent has been viewed by the current user. */
  isViewed?: boolean;
  /** Whether this talent has been discarded by the current user. */
  isDiscarded?: boolean;
  /** Domain this talent belongs to (e.g. SWE, STEM) - used for grouping in saved/viewed/discarded tabs */
  domain?: string;
  /** Type of work / Expertise (e.g., RL GYM, MLE bench, SWE bench, etc.) */
  typeOfWork?: string | null;
  /** Field (STEM profiles, e.g. Physics, Chemistry) */
  field?: string | null;
  /** Tags (Finance profiles, e.g. Investment Banking, Private Equity) */
  tags?: string[] | null;
  /** Google Scholar metrics (returned for STEM talent) */
  scholarVerify?: {
    url?: string | null;
    citations?: number | null;
    hIndex?: number | null;
    i10Index?: number | null;
  } | null;
  /** GitHub data (returned for SWE talent) */
  githubData?: {
    contributions?: number | null;
    repos?: number | null;
    stars?: number | null;
  } | null;
}

// Legacy alias for backward compatibility
export type SoftwareEngineer = SoftwareTalent;

/** Parsed request body returned from backend after GPT parsing (used for page 2+ to avoid re-parsing). */
export interface ParsedRequestBody {
  domain?: DomainFilter;
  countries?: string[];
  yearOfExperience?: YearOfExperienceRange;
  education?: string[];
  school?: string;
  certificates?: string;
  skills?: SkillSearchFilter[];
  researchPapersCount?: number;
  naturalLanguages?: string[];
  programmingLanguages?: string[];
  companies?: string[];
  typeOfWork?: string;
  field?: string;
  domainFilter?: string;
  page?: number;
  pageSize?: number;
  query?: string; // Original query if GPT couldn't parse it
  excludeViewed?: boolean;
  excludeSaved?: boolean;
  excludeDiscarded?: boolean;
  onlyViewed?: boolean;
  onlySaved?: boolean;
  onlyDiscarded?: boolean;
  pastCompanies?: string[];
  currentCompanies?: string[];
  titles?: string[];
  hasGithubData?: boolean;
  countriesExclude?: string[];
  companiesExclude?: string[];
  skillsExclude?: string[];
  hourlyRate?: YearOfExperienceRange;
  schools?: string[];
}

/** Structured filters the backend actually applied (frontend filters merged with
 *  anything the LLM extracted from a free-text query). Used to hydrate the UI. */
export interface AppliedFilters {
  countries?: string[];
  countriesExclude?: string[];
  yearOfExperience?: YearOfExperienceRange;
  hourlyRate?: YearOfExperienceRange;
  education?: string[];
  schools?: string[];
  certificates?: string;
  skills?: SkillSearchFilter[];
  skillsExclude?: string[];
  companies?: string[];
  companiesExclude?: string[];
  pastCompanies?: string[];
  currentCompanies?: string[];
  titles?: string[];
  hasGithubData?: boolean;
  researchPapersCount?: number;
  naturalLanguages?: string[];
  programmingLanguages?: string[];
  typeOfWork?: string;
  field?: string;
  domainFilter?: string;
  excludeViewed?: boolean;
  excludeSaved?: boolean;
  excludeDiscarded?: boolean;
  onlyViewed?: boolean;
  onlySaved?: boolean;
  onlyDiscarded?: boolean;
}

export interface SearchResponse {
  success: boolean;
  message: string;
  engineers: SoftwareTalent[]; // Backend still uses 'engineers' in response
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  /** Structured filters actually applied — used to hydrate the filter panel + URL. */
  appliedFilters?: AppliedFilters;
  /** Debug info including the final request body after GPT parsing (use for page 2+). */
  debugger?: {
    queryObject: unknown;
    requestBody: ParsedRequestBody;
  };
}
